import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCounters } from '../../contexts/CounterContext';
import { useUser } from '../../contexts/UserContext';
import SubNavbar from './SubNavbar';
import TokenBalance from '../common/TokenBalance';
import { api } from '../../utils/apiClient';
import { setLocalStorageItem } from '../../utils/localStorage';
import i18n from '../../i18n/i18n';
import Tooltip from 'bootstrap/js/dist/tooltip';
import { isOwnerUrlIdentifier } from '../../utils/urlUtils';
import { useTooltips } from '../../utils/tooltipUtils';
import { createLogoutOverlay } from '../../utils/logoutUtils';

/**
 * Main navigation component that displays:
 * 1. The main application navbar (always visible)
 * 2. The counter-specific SubNavbar (only when viewing a counter)
 */
function Navbar() {
  const { t } = useTranslation();
  const { counters, refreshCounters } = useCounters();
  const { user, isAdmin, isLoggedIn, logout, signup } = useUser();
  const { counterId } = useParams<{ counterId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const collapseRef = useRef<HTMLDivElement>(null);
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  
  // Extract counterId from URL path as backup if params don't work
  const pathParts = location.pathname.split('/').filter(Boolean);
  const counterIdentifier = pathParts.length > 0 ? pathParts[0] : null;
  
  // Use either the params or the extracted ID from path
  const effectiveCounterId = counterId || counterIdentifier;
  
  // Check if we're using an owner URL
  const [isOwnerUrl, setIsOwnerUrl] = useState(false);
  const [countersLoaded, setCountersLoaded] = useState(false);
  
  // Force refresh counters for certain paths
  useEffect(() => {
    if (effectiveCounterId && counters.length === 0) {
      refreshCounters();
    }
  }, [effectiveCounterId, counters.length, refreshCounters]);
  
  // Ensure counters are loaded
  useEffect(() => {
    if (counters.length > 0) {
      setCountersLoaded(true);
    } else {
      // If no counters loaded, try to refresh them
      refreshCounters();
    }
  }, [counters, refreshCounters]);
  
  // Determine if we're using an owner URL
  useEffect(() => {
    const checkIfOwnerUrl = () => {
      if (!effectiveCounterId || !countersLoaded) return;
      
      // Use the consistent utility to determine if this is an owner URL
      const isOwner = isOwnerUrlIdentifier(effectiveCounterId, counters);
      setIsOwnerUrl(isOwner);
    };
    
    checkIfOwnerUrl();
  }, [effectiveCounterId, counters, countersLoaded]);
  
  // Check if we're in a counter context by examining the URL path
  const isInCounterContext = pathParts.length > 0 && 
    !['counters', 'settings', 'about', 'counter-deleted', 'counter-not-found'].includes(pathParts[0]);
  
  // Check if we're on the counters page
  const isCountersPage = location.pathname === '/counters';
  
  // Check if we're on the about page
  const isAboutPage = location.pathname === '/about';
  
  // Check if we're on the settings page
  const isSettingsPage = location.pathname === '/settings';

  // Check if we're on the counter-not-found page
  const isCounterNotFoundPage = location.pathname === '/counter-not-found';
  
  // Check if we're on the performance page
  const isPerformancePage = location.pathname === '/performance';
  
  // Close navbar when navigating to a new page
  useEffect(() => {
    if (!isNavCollapsed) {
      setIsNavCollapsed(true);
    }
  }, [location.pathname]);
  
  // Check if the counter exists in our counters list
  const counterExists = countersLoaded && effectiveCounterId && 
    (counters.some(c => c._id === effectiveCounterId) || 
     counters.some(c => c.ownerUrl === effectiveCounterId));
  
  // Find admin badge and counters link
  const showAdminMode = isAdmin;
  
  // Use our tooltip utility for safe tooltip management
  useTooltips([isLoggedIn, isAdmin, location.pathname]);
  
  // Effect to listen for the openLoginModal custom event
  useEffect(() => {
    const handleOpenLoginModalEvent = () => {
      // Navigate to login page with state to redirect back to current page
      const redirectTo = location.pathname;
      navigate('/login', { state: { from: { pathname: redirectTo } } });
    };

    window.addEventListener('openLoginModal', handleOpenLoginModalEvent);
    return () => {
      window.removeEventListener('openLoginModal', handleOpenLoginModalEvent);
    };
  }, [location.pathname, navigate]);
  
  // Effect to fetch user language from server when logged in
  useEffect(() => {
    const fetchUserLanguageFromServer = async () => {
      if (user.email && user.token) {
        try {
          const response = await api.user.getSettings(user.email);
          if (response.success && response.settings && response.settings.language) {
            const serverLanguage = response.settings.language;
            
            if (serverLanguage !== i18n.language) {
              i18n.changeLanguage(serverLanguage);
            }
          }
        } catch (error) {
          console.error('Error fetching language from server:', error);
        }
      }
    };
    
    fetchUserLanguageFromServer();
  }, [user.email, user.token, i18n]);
  
  // Don't render anything if we can't determine if we're in a counter context
  if (!location.pathname) {
    return null;
  }

  return (
    <>
      {/* Main navbar - always visible */}
      <nav className="navbar navbar-expand-md navbar-dark bg-dark shadow-sm py-2">
        <div className="container">
          <Link className="navbar-brand d-flex align-items-center" to="/">
            <img 
              src="/images/favicon/favicon.svg" 
              alt="TapDonkey Logo"
              className="me-2" 
              width="24" 
              height="24"
            />
            <span className="fw-bold">{t('app.title')}</span>
          </Link>
          
          {/* Token Balance - mobile version only */}
          {isLoggedIn && (
            <div className="d-md-none me-auto ms-2">
              <TokenBalance email={user.email} token={user.token} />
            </div>
          )}
          
          <button 
            className="navbar-toggler border-0 p-1" 
            type="button"
            onClick={() => setIsNavCollapsed(!isNavCollapsed)}
            aria-controls="navbarNav"
            aria-expanded={!isNavCollapsed}
            aria-label={t('navbar.toggleNavigationAriaLabel', 'Toggle navigation')}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          
          <div 
            className={`collapse navbar-collapse ${isNavCollapsed ? '' : 'show'}`} 
            id="navbarNav"
            ref={collapseRef}
          >
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <Link 
                  className={`nav-link px-3 py-2 ${isCountersPage ? 'active rounded' : ''}`} 
                  to="/counters"
                >
                  <i className="bi bi-grid-3x3-gap me-1"></i>
                  {t('counters.title', 'Tap Counters')}
                </Link>
              </li>
              
              <li className="nav-item">
                <Link 
                  className={`nav-link px-3 py-2 ${isAboutPage ? 'active rounded' : ''}`} 
                  to="/about"
                >
                  <i className="bi bi-info-circle me-1"></i>
                  {t('common.about', 'About')}
                </Link>
              </li>
              
              {/* Performance link - only visible to admin users */}
              {showAdminMode && (
                <li className="nav-item">
                  <Link 
                    className={`nav-link px-3 py-2 ${isPerformancePage ? 'active rounded' : ''}`} 
                    to="/performance"
                  >
                    <i className="bi bi-speedometer2 me-1"></i>
                    {t('performance.title', 'Performance')}
                  </Link>
                </li>
              )}
            </ul>
            
            <ul className="navbar-nav">
              {/* Token Balance - desktop version only */}
              {isLoggedIn && (
                <li className="nav-item d-none d-md-block">
                  <TokenBalance email={user.email} token={user.token} />
                </li>
              )}
              
              {/* Only show Settings link when user is logged in */}
              {isLoggedIn && (
                <li className="nav-item">
                  <Link 
                    className={`nav-link px-3 py-2 ${isSettingsPage ? 'active rounded' : ''}`}
                    to="/settings"
                  >
                    <i className="bi bi-gear me-1"></i>
                    {t('settings.title', 'Settings')}
                  </Link>
                </li>
              )}
              
              {/* Auth Links - Show login/signup when not logged in, show logout when logged in */}
              {!isLoggedIn ? (
                <>
                  <li className="nav-item">
                    <Link 
                      className="nav-link px-3 py-2"
                      to="/login"
                    >
                      <i className="bi bi-box-arrow-in-right me-1"></i>
                      {t('auth.login', 'Login')}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link 
                      className="nav-link px-3 py-2"
                      to="/signup"
                    >
                      <i className="bi bi-person-plus me-1"></i>
                      {t('auth.signup', 'Signup')}
                    </Link>
                  </li>
                </>
              ) : (
                <li className="nav-item">
                  <button 
                    className="nav-link px-3 py-2 border-0 bg-transparent"
                    onClick={(e) => {
                      e.preventDefault();
                      // Create an overlay before logout to prevent flashing
                      createLogoutOverlay();
                      // Call logout after a tiny delay
                      setTimeout(() => {
                        logout();
                      }, 10);
                    }}
                  >
                    <i className="bi bi-box-arrow-right me-1"></i>
                    {t('auth.logout', 'Logout')}
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>
      
      {/* Only render SubNavbar when we're in a counter context, the counter exists, and not on counter-not-found page */}
      {isInCounterContext && effectiveCounterId && counterExists && !isCounterNotFoundPage && (
        <SubNavbar counterId={effectiveCounterId} isOwnerUrl={isOwnerUrl} />
      )}
    </>
  );
}

export default Navbar; 